/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

/**
 * Ths module contains types and utility functions used by the hook that
 * switches assistants.
 *
 * It contains methods that dispatch global events that the WXO host page
 * listens to in order to load the list of New IA assistants that it uses
 * to populate a drop-down on the WXO header bar.
 */

export const enum AssistantsMfeEvent {
  // Event issued by the WXO host page when the user has requested to create
  // a new assistant (by clicking on the "Create assistant" item on the
  // drop-down on the WXO host page header bar).
  // The WXA MFE listens to this event and opens the modal to create an
  // assistant.
  ASSISTANTS_CREATION = 'assistants-creation',

  // Event issued by the WXA MFE when the list of New IA assistants has loaded
  // or has been modified.
  // The WXO host page listens to this event and renders a drop-down on the
  // WXO host page header bar.
  ASSISTANTS_LOAD = 'assistants-load',

  // Event issued by the WXO host page when the user has selected one of the
  // assistants on the drop-down on the WXO host page header bar.
  // The WXA MFE listens to this event and changes the currently-active
  // assistant to be the one the user has selected in the drop-down.
  ASSISTANTS_SELECTION = 'assistants-selection'
}

type AssistantData = {
  name: string;
  assistantId: string;
};

type AssistantsEventData = {
  assistants: AssistantData[];
  maxAssistantsReached: boolean;
};

export type AssistantEventDetail = {
  assistant: AssistantData;
};

/**
 * Dispatches the event that loads New IA assistants in a drop-down on the
 * WXO host page header bar.
 *
 * @param detail Object containing the array of New IA assistants and a
 * boolean that determines whether the "Create assistant" button should be
 * shown in the drop-down
 */
export const sendAssistantsLoadEvent = (detail: AssistantsEventData): void => {
  window.dispatchEvent(
    new CustomEvent<AssistantsEventData>(AssistantsMfeEvent.ASSISTANTS_LOAD, {
      detail
    })
  );
};

/**
 * Method that clears the drop-down on the WXO host page header bar by
 * passing an empty array as the list of New IA assistants.
 */
export const sendClearAssistantsEvent = (): void => {
  sendAssistantsLoadEvent({
    assistants: [],
    maxAssistantsReached: false
  });
};
